<template>
    <div v-if="chartData" class="adapters_chart__container h-full">
        <AdaptersLoader :loading="isChartLoading" />
        <ChartPrimeVue
            v-if="!pdf || watermarkReady"
            :type="chartType"
            :options="getOptions"
            :data="getData"
            @loaded="setChartLoaded"
            ref="chart"
            style="height: 300px;"
            :pt="pt"
            :plugins="getPlugins"
        />
    </div>
</template>

<script>
import ChartPrimeVue from 'primevue/chart';
import AdaptersLoader from "./Loader.vue";
import "../../plugins/chart";

// Линейный график
const LINE_CHART_TYPE = 'line';
// Круговой график
const DOUGHNUT_CHART_TYPE = 'doughnut';
// График Бар
const BAR_CHART_TYPE = 'bar';

// Максимальный размер кастомного водяного знака в PDF (пропорции сохраняются)
const WATERMARK_MAX_WIDTH = 100;
const WATERMARK_MAX_HEIGHT = 50;

function fitWatermarkSize(naturalWidth, naturalHeight, maxWidth = WATERMARK_MAX_WIDTH, maxHeight = WATERMARK_MAX_HEIGHT) {
    if (!naturalWidth || !naturalHeight) {
        return null;
    }
    const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight, 1);
    return {
        width: Math.round(naturalWidth * scale),
        height: Math.round(naturalHeight * scale),
    };
}

export default {
    name: "AdaptersChart",
    emits: ['watermark-ready'],
    props: {
        chartData: {
            type: [Object, null],
            required: true,
        },
        chartOptions: {
            type: Object,
            required: false,
            default: {},
        },
        // массив локальных плагинов
        chartPlugins: {
            type: Array,
            required: false,
            default: [],
        },
        chartType: {
            type: String,
            required: false,
            default: 'line',
        },
        useWatermark: {
            type: Boolean,
            required: false,
            default: true,
        },
        pdf: {
            type: Boolean,
            required: false,
            default: false,
        },
        customWaterMark: {
            type: String,
            required: false,
            default: ''
        },
        // выравнивание оси X по разным datasets, когда наборы X у datasets различаются
        alignDatasets: {
            type: Boolean,
            required: false,
            default: false,
        },
        pt: {
            type: Object,
            required: false,
        },
    },
    data() {
        return {
            loading: true,
            watermarkSize: null,
            watermarkReady: false,
            watermarkLoadId: 0,
            pdfReadyEmitted: false,
            defaultChartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                // отключаем анимацию для графиков, особого смысла в ней нет, но у PDF отображаются только точки
                animation: {
                    duration: 0,
                },
            },
            defaultLineChartOptions: {
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: true,
                    datalabels: {
                        display: false,
                    },
                    hideAll: false,
                },
                interaction: {
                    mode: 'index',
                    intersect: false,
                },
                elements: {
                    point: {
                        hitRadius: 2,
                        radius: 1,
                    },
                },
                scales: {
                    x: {
                        display: true,
                        scaleLabel: {
                            display: true
                        },
                        ticks: {
                            display: true,
                        }
                    },
                    y: {
                        display: true,
                        scaleLabel: {
                            display: true
                        }
                    }
                },
            },
            defaultDoughnutChartOptions: {
                plugins: {
                    legend: {
                        display: true,
                        position: 'right',
                        labels: {
                            boxWidth: 12,
                        },
                    },
                    datalabels: {
                        display: true,
                        color: '#fff',
                    },
                },
            },
            defaultBarChartOptions: {
                datasets: {
                    bar: {
                        maxBarThickness: 50,
                    },
                },
                interaction: {
                    mode: 'index',
                    intersect: false
                },
                plugins: {
                    tooltip: {
                        mode: 'index',
                        intersect: false
                    },
                    legend: {
                        position: 'top',
                    },
                    datalabels: {
                        display: false
                    },
                },
            },
        };
    },
    computed: {
        getData() {
            if (!this.alignDatasets) {
                return this.chartData;
            }

            /**
             * Доработка для типов графиков LINE, ломалось наведение при различных значениях xAxes
             */
            let datasets = this.chartData.datasets;
            // Собираем все уникальные значения x
            const allXValues = [...new Set(datasets.flatMap(dataset => dataset.data.map(point => point.x)))].sort();

            // Выровняем данные
            const alignedDatasets = datasets.map(dataset => {
                const dataMap = new Map(dataset.data.map(point => [point.x, point.y]));
                return {
                    ...dataset,
                    data: allXValues.map(x => ({
                        x,
                        y: dataMap.get(x) ?? null, // Заполняем null, если данные отсутствуют
                    })),
                };
            });

            return {
                labels: allXValues,
                datasets: alignedDatasets
            };
        },
        getOptions() {
            let options = this.getDefaultChartOptions();
            if (this.useWatermark) {
                options.watermark = this.getWatermark();
            }
            return _.merge(options, this.chartOptions);
        },
        getPlugins() {
            return this.chartPlugins;
        },
        isChartLoading() {
            if (this.pdf && !this.watermarkReady) {
                return true;
            }
            return this.loading;
        },
    },
    methods: {
        getDefaultChartOptions() {
            switch (this.chartType) {
                case LINE_CHART_TYPE:
                    return _.merge(this.defaultChartOptions, this.defaultLineChartOptions);
                case DOUGHNUT_CHART_TYPE:
                    return _.merge(this.defaultChartOptions, this.defaultDoughnutChartOptions);
                case BAR_CHART_TYPE:
                    return _.merge(this.defaultChartOptions, this.defaultBarChartOptions);
                default:
                    return this.defaultChartOptions;
            }
        },
        setChartLoaded() {
            this.loading = false;
            this.tryEmitPdfReady();
        },
        loadWatermarkSize() {
            const src = this.customWaterMark;
            this.pdfReadyEmitted = false;

            if (!this.pdf || !src) {
                this.watermarkSize = null;
                this.watermarkReady = true;
                this.tryEmitPdfReady();
                return;
            }

            const loadId = ++this.watermarkLoadId;
            this.watermarkSize = null;
            this.watermarkReady = false;

            const img = new Image();
            img.onload = () => {
                if (loadId !== this.watermarkLoadId) {
                    return;
                }
                this.watermarkSize = fitWatermarkSize(img.naturalWidth, img.naturalHeight);
                this.watermarkReady = true;
                this.tryEmitPdfReady();
            };
            img.onerror = () => {
                if (loadId !== this.watermarkLoadId) {
                    return;
                }
                this.watermarkSize = null;
                this.watermarkReady = true;
                this.tryEmitPdfReady();
            };
            img.src = src;
        },
        tryEmitPdfReady() {
            if (!this.pdf || this.pdfReadyEmitted || !this.watermarkReady || this.loading) {
                return;
            }

            const chart = this.$refs.chart?.getChart();
            const watermarkImage = chart?.watermark?.image;

            if (watermarkImage && typeof watermarkImage === 'object' && !watermarkImage.complete) {
                watermarkImage.addEventListener('load', () => this.emitPdfReady(), { once: true });
                return;
            }

            this.emitPdfReady();
        },
        emitPdfReady() {
            if (!this.pdf || this.pdfReadyEmitted) {
                return;
            }
            this.pdfReadyEmitted = true;
            this.$nextTick(() => {
                this.$emit('watermark-ready');
            });
        },
        getWatermark() {
            let specialOptions = this.pdf
                ? {
                    image: this.customWaterMark,
                    opacity: 0.2,
                    x: 10,
                    y: 10,
                    ...(this.watermarkSize || {}),
                    alignX: "right",
                    alignY: "top",
                }
                : this.getKeyssoWaterMark();

            return {...specialOptions, ...{

                // if true, aligns the watermark to the inside of the chart area (where the lines are)
                // (where the lines are)
                // if false, aligns the watermark to the inside of the canvas
                // see samples/alignToChartArea.html
                alignToChartArea: true,

                // determines whether the watermark is drawn on top of or behind the chart
                // valid values: "front", "back", "between"
                position: "back",
            }};
        },
        getKeyssoWaterMark() {
            let url = new URL(window.location.href);
            switch (this.chartType) {
                case LINE_CHART_TYPE:
                case BAR_CHART_TYPE:
                    return {
                        image: url.origin + "/i/black-sign-chart.png",
                        x: 10,
                        y: 10,
                        alignX: "right",
                        alignY: "top",
                    };
                case DOUGHNUT_CHART_TYPE:
                    return {
                        image: url.origin + "/ms-icon-70x70.png",
                        x: 0,
                        y: 0,
                        opacity: 0.2,
                        alignX: "middle",
                        alignY: "middle",
                    };
            }
        },
        toBase64Image() {
            return this.$refs.chart.getChart().toBase64Image();
        },
    },
    watch: {
        customWaterMark: {
            immediate: true,
            handler() {
                this.loadWatermarkSize();
            },
        },
        pdf() {
            this.loadWatermarkSize();
        },
    },
    components: {
        ChartPrimeVue,
        AdaptersLoader,
    },
}
</script>
